"use strict";
/**
 * Remove this file once we move frontend-toolkit into an npm module
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateService = exports.DateFormat = void 0;
/**
 * @enum DateFormat
 * This enum provides the different formatting options supported by the `dateToString` method of DateService.
 */
var DateFormat;
(function (DateFormat) {
    DateFormat[DateFormat["YYYY_MM_DD"] = 0] = "YYYY_MM_DD";
    DateFormat[DateFormat["MM_DD_YYYY"] = 1] = "MM_DD_YYYY";
    DateFormat[DateFormat["SHORT_DATE"] = 2] = "SHORT_DATE";
    DateFormat[DateFormat["LONG_DATE"] = 3] = "LONG_DATE";
    DateFormat[DateFormat["DATE_WITH_TIME_AND_TIMEZONE"] = 4] = "DATE_WITH_TIME_AND_TIMEZONE";
    DateFormat[DateFormat["DD_MM_YYYY"] = 5] = "DD_MM_YYYY";
    DateFormat[DateFormat["DATE_WITH_AT_TIME"] = 6] = "DATE_WITH_AT_TIME";
})(DateFormat || (exports.DateFormat = DateFormat = {}));
/**
 * @class DateService
 * used to work with Dates, leverages INTL methods (browser only) and should be used instead of moment or time.js
 */
class DateService {
    static formatters = {
        [DateFormat.YYYY_MM_DD]: DateService.YYYY_MM_DD,
        [DateFormat.MM_DD_YYYY]: DateService.MM_DD_YYYY,
        [DateFormat.SHORT_DATE]: DateService.SHORT_DATE,
        [DateFormat.LONG_DATE]: DateService.LONG_DATE,
        [DateFormat.DATE_WITH_TIME_AND_TIMEZONE]: DateService.DATE_WITH_TIME_AND_TIMEZONE,
        [DateFormat.DD_MM_YYYY]: DateService.DD_MM_YYYY,
        [DateFormat.DATE_WITH_AT_TIME]: DateService.DATE_WITH_AT_TIME,
    };
    /**
     * @method dateToString
     * @description Formats a given date with the provided formatter.
     * @param date A date object that you wish to format.
     * @param format @enum DateFormat
     * @returns string
     */
    static dateToString(date, format) {
        if (!date)
            return '';
        const formatter = DateService.formatters[format];
        if (!formatter) {
            return 'Err: unspecified formatter.';
        }
        return formatter(date);
    }
    static format(date, format) {
        if (!date)
            return '';
        const formatter = DateService.formatters[format];
        if (!formatter) {
            return 'Err: unspecified formatter.';
        }
        return formatter(date);
    }
    /**
     * @method stringToDate
     * @description Converts a string into a date.
     * @param dateString A string in date format that you need to convert to a js Date.
     * @param format @enum DateFormat
     * @returns Date
     */
    static stringToDate(dateString, format) {
        const date = new Date(dateString.length === 19 ? dateString + '+00:00' : dateString);
        if (format === DateFormat.YYYY_MM_DD) {
            return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        }
        return date;
    }
    /**
     * @method formatLocaleDateTime
     * @description Converts a date to an international datetime string based on locale.
     * @param dateString A string in date format that you need to convert to a specific locale.
     * @param locale A string representing the desired locale, ex. 'en-us'
     * @param options @interface DateTimeFormatOptions
     * @returns string
     */
    static formatLocaleDateTime(date, locale, options) {
        const dateTimeFormat = new Intl.DateTimeFormat(locale, options).format(date);
        return dateTimeFormat;
    }
    /**
     * @method compareDates
     * @description Compares a calculated date that is X days in the past or future, from the given original date, against the given compare date and returns a boolean if greater or less than.
     * @param compareDate The date used to compare against the calculated date.
     * @param originalDate Original date used to calculate the new date to compare against.
     * @param daysApart Number of days in the past or future from the original date.
     * @param comparator 'greater' | 'less'| 'greater_or_equal' | 'less_or_equal' -> Will determine how to compare the compare date against the calculated date.
     * @param requestedDateInPast Is the calculated date before the original date? If true, the calculated date will be less than the original date.
     * @returns boolean
     */
    static compareDates(compareDate, originalDate, daysApart, comparator, requestedDateInPast) {
        let calculatedDate;
        if (requestedDateInPast) {
            calculatedDate = this.removeDaysFromDate(daysApart, originalDate);
        }
        else {
            calculatedDate = this.addDaysToDate(daysApart, originalDate);
        }
        switch (comparator) {
            case 'greater':
                return compareDate > calculatedDate;
            case 'less':
                return compareDate < calculatedDate;
            case 'greater_or_equal':
                return compareDate >= calculatedDate;
            case 'less_or_equal':
                return compareDate <= calculatedDate;
            default:
                return false;
        }
    }
    // private methods below are used connected as formatter functions via the DateFormat enum.
    // mark as private static, wire up via the `formatters` property and then add tests for dateToString
    static YYYY_MM_DD(date) {
        // Make attributes Zero padded
        const dateArr = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC' })
            .format(date)
            .split('/')
            .map((n) => (n.length === 1 ? `0${n}` : n));
        // Return YYYY-MM-DD format
        return `${dateArr[2]}-${dateArr[0]}-${dateArr[1]}`;
    }
    static MM_DD_YYYY(date) {
        // Make attributes Zero padded
        const dateArr = new Intl.DateTimeFormat('en-US', {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
            .format(date)
            .split('/')
            .map((n) => (n.length === 1 ? `0${n}` : n));
        // Return MM/DD/YYYY format
        return `${dateArr[0]}/${dateArr[1]}/${dateArr[2]}`;
    }
    static SHORT_DATE(date) {
        const formatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
        };
        return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    }
    static LONG_DATE(date) {
        const formatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC',
        };
        return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    }
    static DATE_WITH_TIME_AND_TIMEZONE(date) {
        const formatOptions = {
            hour: 'numeric',
            minute: 'numeric',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZoneName: 'short',
        };
        return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    }
    static DD_MM_YYYY(date) {
        const options = {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        };
        return new Intl.DateTimeFormat('en-US', options).format(date);
    }
    static DATE_WITH_AT_TIME(date) {
        const formatOptions = {
            hour: 'numeric',
            minute: 'numeric',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeZoneName: 'short',
        };
        const dateStr = new Intl.DateTimeFormat('en-US', formatOptions).format(date);
        const [datePart, time] = dateStr.split(',');
        // creatre an array that looks like so...['', '7:00', 'PM', 'EST']
        const timeParts = time.split(' ');
        // Trim parts we do not need
        timeParts.pop();
        timeParts.shift();
        return `${datePart} at ${timeParts.join(' ')}`;
    }
    static addDaysToDate = (daysToAdd, date) => {
        const MilliSecondsInADay = 86400000;
        const targetDate = date ? date.getTime() : Date.now();
        return new Date(targetDate + daysToAdd * MilliSecondsInADay);
    };
    static removeDaysFromDate = (daysToRemove, date) => {
        const MilliSecondsInADay = 86400000;
        const targetDate = date ? date.getTime() : Date.now();
        return new Date(targetDate - daysToRemove * MilliSecondsInADay);
    };
    static sortStringDates = (list, sortAsc, key) => list.sort((a, b) => sortAsc
        ? DateService.stringToDate(key ? a[key] : a, DateFormat.MM_DD_YYYY).getTime() -
            DateService.stringToDate(key ? b[key] : b, DateFormat.MM_DD_YYYY).getTime()
        : DateService.stringToDate(key ? b[key] : b, DateFormat.MM_DD_YYYY).getTime() -
            DateService.stringToDate(key ? a[key] : a, DateFormat.MM_DD_YYYY).getTime());
    static datesAreSameDay = (first, second) => first.getFullYear() === second.getFullYear() &&
        first.getMonth() === second.getMonth() &&
        first.getDate() === second.getDate();
}
exports.DateService = DateService;
